import React from 'react';
import { Circle, G, Line, Polygon, Rect, Text as SvgText } from 'react-native-svg';
import { DiagramCanvas } from '../DiagramCanvas';
import { FormulaTag } from '../FormulaTag';
import { LeaderLine } from '../LeaderLine';
import { DT } from '../tokens';

/**
 * Rectangular duct face traverse grid (5 × 4) per ASHRAE log-Tchebycheff.
 * Shows numbered sample points, equal-area zone lines, pitot probe at point #1,
 * and W × H dimension lines.
 */
export function DiagramPitotTraverseGrid() {
  const rows = 4, cols = 5;
  const x0 = 70, y0 = 55, w = 200, h = 130;

  // Equal-area zone divisions (visual aid)
  const colDiv = Array.from({ length: cols + 1 }, (_, i) => x0 + (w / cols) * i);
  const rowDiv = Array.from({ length: rows + 1 }, (_, i) => y0 + (h / rows) * i);

  // Sample-point coordinates (centers of each zone)
  const points: { x: number; y: number; n: number }[] = [];
  let n = 1;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      points.push({
        x: x0 + (w / cols) * (c + 0.5),
        y: y0 + (h / rows) * (r + 0.5),
        n: n++,
      });
    }
  }

  const probe = points[0]; // top-left first point

  return (
    <DiagramCanvas>
      {/* Duct face outline */}
      <Rect x={x0} y={y0} width={w} height={h} fill={DT.fill} stroke={DT.stroke} strokeWidth="2" />

      {/* Equal-area zone grid */}
      {colDiv.slice(1, -1).map((cx, i) => (
        <Line key={`c${i}`} x1={cx} y1={y0} x2={cx} y2={y0 + h}
          stroke={DT.dim} strokeWidth="0.6" strokeDasharray="2,3" opacity={0.7} />
      ))}
      {rowDiv.slice(1, -1).map((cy, i) => (
        <Line key={`r${i}`} x1={x0} y1={cy} x2={x0 + w} y2={cy}
          stroke={DT.dim} strokeWidth="0.6" strokeDasharray="2,3" opacity={0.7} />
      ))}

      {/* Sample points */}
      {points.map(p => (
        <G key={p.n}>
          <Circle cx={p.x} cy={p.y} r={3} fill={DT.varColor} opacity={0.9} />
          {p.n <= 6 ? (
            <SvgText x={p.x + 5} y={p.y - 4} fill={DT.desc}
              fontSize="7" fontFamily={DT.mono}>{p.n}</SvgText>
          ) : null}
        </G>
      ))}

      {/* Pitot probe inserted at point #1 (from top) */}
      <Line x1={probe.x} y1={y0 - 22} x2={probe.x} y2={probe.y + 4}
        stroke={DT.leader} strokeWidth="3" />
      <Line x1={probe.x - 10} y1={probe.y} x2={probe.x} y2={probe.y}
        stroke={DT.leader} strokeWidth="3" />
      <Circle cx={probe.x - 10} cy={probe.y} r={2.5} fill={DT.varColor} />

      {/* Dimensions: W (top) and H (right) */}
      <Line x1={x0} y1={y0 - 35} x2={x0 + w} y2={y0 - 35} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x0} y1={y0 - 39} x2={x0} y2={y0 - 31} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x0 + w} y1={y0 - 39} x2={x0 + w} y2={y0 - 31} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={x0 + w / 2} y={y0 - 39} fill={DT.dim}
        fontSize="9" fontFamily={DT.mono} textAnchor="middle">W</SvgText>

      <Line x1={x0 + w + 14} y1={y0} x2={x0 + w + 14} y2={y0 + h}
        stroke={DT.dim} strokeWidth="1" />
      <Line x1={x0 + w + 10} y1={y0} x2={x0 + w + 18} y2={y0} stroke={DT.dim} strokeWidth="1" />
      <Line x1={x0 + w + 10} y1={y0 + h} x2={x0 + w + 18} y2={y0 + h} stroke={DT.dim} strokeWidth="1" />
      <SvgText x={x0 + w + 22} y={y0 + h / 2 + 3} fill={DT.dim}
        fontSize="9" fontFamily={DT.mono}>H</SvgText>

      {/* Sample count label */}
      <SvgText x={x0 + w / 2} y={y0 + h + 18} fill={DT.desc}
        fontSize="9" fontFamily={DT.mono} textAnchor="middle">{rows} × {cols} = {rows * cols} sample points</SvgText>

      {/* Labels */}
      <LeaderLine x1={probe.x} y1={y0 - 18} x2={20} y2={210} varName="VP_i" desc="Velocity press." anchor="start" />
      <LeaderLine x1={points[points.length - 1].x} y1={points[points.length - 1].y}
        x2={310} y2={210} varName="V_avg" desc="Σ(4005√VP)/n" anchor="end" />

      <FormulaTag formula="V_avg = Σ(4005√VP_i) / n   →   CFM = V_avg × A" />
    </DiagramCanvas>
  );
}
